import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/common/ui/select";

import { EDITABLE_SCOPE_KINDS, type EditableScopeKind, scopeKindLabel } from "./scope";

export interface ScopeSelectorProps {
  readonly kind: EditableScopeKind;
  readonly id: string;
  readonly onKindChange: (kind: EditableScopeKind) => void;
  readonly onIdChange: (id: string) => void;
}

/**
 * Selects the scope a grant applies to: Platform (no id) or Enterprise / Facility
 * (org unit) / User by id. The id-by-value input is the MVP; ENR-1129 follow-up
 * swaps it for an org-unit-tree picker + user search (the scope resolves to the
 * same `ScopeRef`).
 */
export function ScopeSelector({
  kind,
  id,
  onKindChange,
  onIdChange,
}: ScopeSelectorProps): JSX.Element {
  return (
    <div className="space-y-3" data-testid="scope-selector">
      <div className="space-y-1.5">
        <Label htmlFor="scope-kind">Scope</Label>
        <Select value={kind} onValueChange={(next) => onKindChange(next as EditableScopeKind)}>
          <SelectTrigger id="scope-kind" data-testid="scope-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EDITABLE_SCOPE_KINDS.map((option) => (
              <SelectItem key={option} value={option}>
                {scopeKindLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {kind !== "platform" && (
        <div className="space-y-1.5">
          <Label htmlFor="scope-id">{scopeKindLabel(kind)} id</Label>
          <Input
            id="scope-id"
            data-testid="scope-id"
            value={id}
            onChange={(event) => onIdChange(event.target.value)}
            placeholder={`${kind} id`}
          />
        </div>
      )}
    </div>
  );
}
